import { Stack, Typography } from '@mui/material';
import React from 'react';
import { useIntl } from 'react-intl';

import Icon from '@components/Icon/Icon';

import {
  ASSESSMENT_RESULT_MAP,
  ASSESSMENT_RISK_MAP,
} from '@constants/assessment';
import { getObservationConfig } from '@constants/fhir';

import { useAssessmentResult } from '@hooks/useAssessmentResult';

import type { ObservationRecord } from '@models/observation';

import { formatVitalSign } from '@utils/format';

import Description from '../Common/Description';

interface TitleProps {
  code: string;
}

const Title = ({ code }: TitleProps) => {
  const { formatMessage } = useIntl();
  const metric = ASSESSMENT_RESULT_MAP.get(code);
  const { loading, getResult } = useAssessmentResult();
  const format = getObservationConfig(code)?.format;

  if (!metric) {
    return null;
  }
  const result = getResult(code);

  const displayMetricValue = () => {
    let formatFn: (val: any) => string;
    let val: ObservationRecord | number | string | { id: string };

    if (result?.observation) {
      // If there is an obs, format it with its units (ex. BP)
      val = result.observation;
      formatFn = (val: ObservationRecord) => formatVitalSign(val, true, format);
    } else if (typeof result?.value === 'number') {
      // If there is a risk number for a metric, format/display it (ex. Anxiety)
      val = result.value;
      formatFn = (val: number) => (format ? format(val) : val.toString());
    } else if (result?.risk) {
      // If there is no obs/risk value for a metric, show the risk level (ex. HDL)
      val = { id: ASSESSMENT_RISK_MAP[result.risk].label };
      formatFn = formatMessage;
    } else {
      val = '-';
      formatFn = (val: string) => val;
    }

    return formatFn(val);
  };

  return (
    <Description
      explanation={formatMessage({ id: metric.description })}
      loading={loading}
      result={
        <Stack alignItems="center" direction="row" spacing={0.5}>
          <Typography
            alignItems="center"
            display="flex"
            sx={{
              // Capitalizes lipid panel's text
              textTransform:
                result?.observation || result?.value ? undefined : 'capitalize',
            }}
          >
            {displayMetricValue()}
          </Typography>
          <Icon
            color={
              result?.risk
                ? ASSESSMENT_RISK_MAP[result.risk].color
                : 'primary.main'
            }
            name="square"
            size="lg"
            type="fas"
          />
        </Stack>
      }
      title={formatMessage({ id: metric.title })}
    />
  );
};

export default Title;
